import { API_BASE, MOCK_MODE } from '../config';
import { forceReauth, getIdTokenClaims, getValidIdToken, refreshAccessToken } from '../auth/auth';
import { ApiError } from './errors';
import { mockApi } from './mock';
import type {
  AdminSummary,
  ApiKeySummary,
  ArchiveUserResult,
  CheckinsResponse,
  CreatedApiKey,
  CreatedUser,
  CurrentAdminIdentity,
  DeleteDeviceResult,
  InviteAdminResult,
  OverviewResponse,
  RegistrationToken,
  RenameDeviceResult,
  RevokeDeviceResult,
  TrailResponse,
} from './types';

export { ApiError };

async function requestJson<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const token = await getValidIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });

  if (res.status === 401) {
    if (retried) {
      await forceReauth();
    }
    try {
      await refreshAccessToken();
    } catch {
      await forceReauth();
    }
    return requestJson<T>(path, init, true);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as { error?: string });
    throw new ApiError(res.status, body.error ?? res.statusText);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const api = {
  overview(): Promise<OverviewResponse> {
    if (MOCK_MODE) return mockApi.overview();
    return requestJson<OverviewResponse>('/portal/overview');
  },

  trail(deviceId: string, hours: number): Promise<TrailResponse> {
    if (MOCK_MODE) return mockApi.trail(deviceId, hours);
    return requestJson<TrailResponse>(
      `/portal/devices/${encodeURIComponent(deviceId)}/trail?hours=${hours}`,
    );
  },

  createUser(displayName: string): Promise<CreatedUser> {
    if (MOCK_MODE) return mockApi.createUser(displayName);
    return requestJson<CreatedUser>('/portal/users', {
      method: 'POST',
      body: JSON.stringify({ display_name: displayName }),
    });
  },

  archiveUser(userId: string): Promise<ArchiveUserResult> {
    if (MOCK_MODE) return mockApi.archiveUser(userId);
    return requestJson<ArchiveUserResult>(
      `/portal/users/${encodeURIComponent(userId)}/archive`,
      { method: 'POST' },
    );
  },

  mintRegistrationToken(userId: string): Promise<RegistrationToken> {
    if (MOCK_MODE) return mockApi.mintRegistrationToken(userId);
    return requestJson<RegistrationToken>(
      `/portal/users/${encodeURIComponent(userId)}/registration-token`,
      { method: 'POST' },
    );
  },

  revokeDevice(deviceId: string): Promise<RevokeDeviceResult> {
    if (MOCK_MODE) return mockApi.revokeDevice(deviceId);
    return requestJson<RevokeDeviceResult>(
      `/portal/devices/${encodeURIComponent(deviceId)}/revoke`,
      { method: 'POST' },
    );
  },

  renameDevice(deviceId: string, deviceName: string): Promise<RenameDeviceResult> {
    if (MOCK_MODE) return mockApi.renameDevice(deviceId, deviceName);
    return requestJson<RenameDeviceResult>(`/portal/devices/${encodeURIComponent(deviceId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ device_name: deviceName }),
    });
  },

  checkins(deviceId: string, hours: number): Promise<CheckinsResponse> {
    if (MOCK_MODE) return mockApi.checkins(deviceId, hours);
    return requestJson<CheckinsResponse>(
      `/portal/devices/${encodeURIComponent(deviceId)}/checkins?hours=${hours}`,
    );
  },

  deleteDevice(deviceId: string): Promise<DeleteDeviceResult> {
    if (MOCK_MODE) return mockApi.deleteDevice(deviceId);
    return requestJson<DeleteDeviceResult>(`/portal/devices/${encodeURIComponent(deviceId)}`, {
      method: 'DELETE',
    });
  },

  async listAdmins(): Promise<AdminSummary[]> {
    if (MOCK_MODE) return mockApi.listAdmins();
    const res = await requestJson<{ admins: AdminSummary[] }>('/portal/admins');
    return res.admins;
  },

  inviteAdmin(email: string, name: string): Promise<InviteAdminResult> {
    if (MOCK_MODE) return mockApi.inviteAdmin(email, name);
    return requestJson<InviteAdminResult>('/portal/admins', {
      method: 'POST',
      body: JSON.stringify({ email, name }),
    });
  },

  removeAdmin(username: string): Promise<void> {
    if (MOCK_MODE) return mockApi.removeAdmin(username);
    return requestJson<void>(`/portal/admins/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    });
  },

  async listApiKeys(): Promise<ApiKeySummary[]> {
    if (MOCK_MODE) return mockApi.listApiKeys();
    const res = await requestJson<{ keys: ApiKeySummary[] }>('/portal/api-keys');
    return res.keys;
  },

  createApiKey(label: string): Promise<CreatedApiKey> {
    if (MOCK_MODE) return mockApi.createApiKey(label);
    return requestJson<CreatedApiKey>('/portal/api-keys', {
      method: 'POST',
      body: JSON.stringify({ label }),
    });
  },

  revokeApiKey(keyId: string): Promise<void> {
    if (MOCK_MODE) return mockApi.revokeApiKey(keyId);
    return requestJson<void>(`/portal/api-keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
    });
  },

  /** Unverified identity of the signed-in admin, for UI decisions only (see getIdTokenClaims). */
  currentAdminIdentity(): CurrentAdminIdentity {
    if (MOCK_MODE) return mockApi.currentAdminIdentity();
    const claims = getIdTokenClaims();
    return {
      username: (claims?.['cognito:username'] as string | undefined) ?? (claims?.sub as string | undefined) ?? null,
      email: (claims?.email as string | undefined) ?? null,
    };
  },
};
